import { jsPDF } from 'jspdf';

class EbesSolarPDFService {
  formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  }

  formatCurrencyInWords(value) {
    const num = parseFloat(value);
    const conversions = {
      50: 'cinquenta reais',
      100: 'cem reais',
      12000: 'doze mil reais',
      24000: 'vinte e quatro mil reais'
    };
    return conversions[num] || '';
  }

  getCompensationModeName(mode) {
    const modes = {
      'geracao_junto_carga': 'GERAÇÃO JUNTO À CARGA',
      'autoconsumo_remoto': 'AUTOCONSUMO REMOTO',
      'geracao_compartilhada': 'GERAÇÃO COMPARTILHADA',
      'multiplas_unidades': 'MÚLTIPLAS UNIDADES CONSUMIDORAS'
    };
    return modes[mode] || mode;
  }

  getPaymentEventName(event) {
    const events = {
      'assinatura': 'no ato da assinatura desse contrato',
      'entrega_modulos': 'quando ENTREGA DOS MÓDULOS SOLARES',
      'instalacao': 'após instalação completa',
      'homologacao': 'após homologação pela concessionária'
    };
    return events[event] || event;
  }

  numberToWords(num) {
    const words = {
      1: 'um', 2: 'dois', 3: 'três', 4: 'quatro', 5: 'cinco',
      6: 'seis', 7: 'sete', 8: 'oito', 9: 'nove', 10: 'dez',
      11: 'onze', 12: 'doze', 20: 'vinte', 50: 'cinquenta'
    };
    return words[num] || num.toString();
  }

  async generateEbesSolarContract(contractData) {
    try {
      const data = contractData.ebesSolar;
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 25;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;
      const lineHeight = 5;
      const paragraphSpacing = 3;

      // Configurações de fonte
      pdf.setFont('times', 'normal');
      pdf.setFontSize(11);

      // Função auxiliar para carregar imagem
      const loadImage = (src) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            
            const dataUrl = canvas.toDataURL('image/png');
            resolve(dataUrl);
          };
          
          img.onerror = () => {
            console.warn('Erro ao carregar imagem:', src);
            reject(new Error('Failed to load image'));
          };
          
          img.src = src + '?t=' + new Date().getTime();
        });
      };

      // Função auxiliar para adicionar logo
      const addLogo = async () => {
        try {
          const imgData = await loadImage('/ebes.png');
          const imgWidth = 30;
          const imgHeight = 15;
          const imgX = (pageWidth - imgWidth) / 2;
          pdf.addImage(imgData, 'PNG', imgX, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 5;
        } catch (error) {
          console.warn('Logo não carregada:', error);
          pdf.setFont('times', 'bold');
          pdf.setFontSize(14);
          const logoText = 'EBES SOLAR';
          const textWidth = pdf.getTextWidth(logoText);
          pdf.text(logoText, (pageWidth - textWidth) / 2, yPosition);
          yPosition += 10;
          pdf.setFont('times', 'normal');
          pdf.setFontSize(11);
        }
      };

      // Função auxiliar para checar espaço e adicionar página
      const checkSpace = (neededSpace) => {
        if (yPosition + neededSpace > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Função para adicionar texto justificado
      const addJustifiedText = (text, indent = 0, isBold = false) => {
        pdf.setFont('times', isBold ? 'bold' : 'normal');
        const maxWidth = contentWidth - indent;
        const lines = pdf.splitTextToSize(text, maxWidth);
        
        lines.forEach((line, index) => {
          checkSpace(lineHeight);
          
          const trimmedLine = line.trim();
          const isLastLine = index === lines.length - 1;
          
          if (!isLastLine && trimmedLine.length > 0) {
            const words = trimmedLine.split(/\s+/);
            
            if (words.length > 1) {
              const wordsWidth = words.reduce((sum, word) => sum + pdf.getTextWidth(word), 0);
              const totalSpace = maxWidth - wordsWidth;
              const gapCount = words.length - 1;
              const spaceWidth = totalSpace / gapCount;
              
              const normalSpaceWidth = pdf.getTextWidth(' ');
              const maxSpaceWidth = normalSpaceWidth * 3;
              
              if (spaceWidth <= maxSpaceWidth) {
                let xPos = margin + indent;
                words.forEach((word, wordIndex) => {
                  pdf.text(word, xPos, yPosition);
                  if (wordIndex < words.length - 1) {
                    xPos += pdf.getTextWidth(word) + spaceWidth;
                  }
                });
              } else {
                pdf.text(trimmedLine, margin + indent, yPosition);
              }
            } else {
              pdf.text(trimmedLine, margin + indent, yPosition);
            }
          } else {
            pdf.text(trimmedLine, margin + indent, yPosition);
          }
          
          yPosition += lineHeight;
        });
        
        yPosition += paragraphSpacing;
      };

      // Função para adicionar título
      const addTitle = (text) => {
        checkSpace(lineHeight * 2);
        pdf.setFont('times', 'bold');
        pdf.setFontSize(13);
        const textWidth = pdf.getTextWidth(text);
        pdf.text(text, (pageWidth - textWidth) / 2, yPosition);
        yPosition += lineHeight + 3;
        pdf.setFontSize(11);
      };

      // Função para adicionar seção
      const addSection = (title) => {
        checkSpace(lineHeight * 2);
        pdf.setFont('times', 'bold');
        pdf.setFontSize(11);
        pdf.text(title.toUpperCase(), margin, yPosition);
        yPosition += lineHeight + 3;
        pdf.setFont('times', 'normal');
      };

      // Função para adicionar tabela (CORRIGIDA)
      const addTable = (data) => {
        const col1Width = contentWidth * 0.45;
        const col2Width = contentWidth * 0.55;

        data.forEach((row, index) => {
          let maxHeight = 8;
          
          if (row.colspan) {
            const lines = pdf.splitTextToSize(row.text, contentWidth - 4);
            maxHeight = Math.max(maxHeight, lines.length * 4 + 4);
          } else {
            const labelLines = pdf.splitTextToSize(row.label || '', col1Width - 4);
            const valueLines = pdf.splitTextToSize(row.value || '', col2Width - 4);
            const neededLines = Math.max(labelLines.length, valueLines.length);
            maxHeight = Math.max(maxHeight, neededLines * 4 + 4);
          }

          checkSpace(maxHeight + 2);

          const cellY = yPosition;

          if (row.colspan) {
            pdf.rect(margin, cellY, contentWidth, maxHeight);
            
            if (row.isHeader) {
              pdf.setFillColor(217, 217, 217);
              pdf.rect(margin, cellY, contentWidth, maxHeight, 'F');
              pdf.rect(margin, cellY, contentWidth, maxHeight);
              pdf.setFont('times', 'bold');
            }

            const lines = pdf.splitTextToSize(row.text, contentWidth - 4);
            let textY = cellY + 5;
            lines.forEach(line => {
              pdf.text(line, margin + 2, textY);
              textY += 4;
            });

            if (row.isHeader) {
              pdf.setFont('times', 'normal');
            }
          } else {
            pdf.rect(margin, cellY, col1Width, maxHeight);
            pdf.rect(margin + col1Width, cellY, col2Width, maxHeight);

            if (row.label) {
              pdf.setFont('times', 'bold');
              const labelLines = pdf.splitTextToSize(row.label, col1Width - 4);
              let textY = cellY + 5;
              labelLines.forEach(line => {
                pdf.text(line, margin + 2, textY);
                textY += 4;
              });
              pdf.setFont('times', 'normal');
            }

            if (row.value) {
              const valueLines = pdf.splitTextToSize(row.value, col2Width - 4);
              let textY = cellY + 5;
              valueLines.forEach(line => {
                pdf.text(line, margin + col1Width + 2, textY);
                textY += 4;
              });
            }
          }

          yPosition += maxHeight;
        });

        // ESPAÇAMENTO APÓS A TABELA (CORRIGIDO)
        yPosition += lineHeight + paragraphSpacing;
      };

      // ==== INÍCIO DO DOCUMENTO ====

      await addLogo();

      addTitle('CONTRATO DE FORNECIMENTO E INSTALAÇÃO DE');
      addTitle('USINA DE MICROGERAÇÃO FOTOVOLTAICA');

      yPosition += 5;

      // Partes
      addJustifiedText(`A EBES ENGENHARIA LTDA- EBES SOLAR, empresa inscrita no CNPJ/MF sob o n.º 02.277.320/0002-08, com endereço na Av Guajajaras, loja 3, Jardim São Cristovão, CEP 65.056-045 nesta Cidade de São Luis, Estado do Maranhão, neste ato representada pelo Sr. MARCUS VINICIUS MELO CHAVES, doravante denominada EBES SOLAR e do outro lado:`);

      const clientInfo = data.clientType === 'pj' 
        ? `empresa inscrita no CNPJ/MF sob o n.º ${data.clientCNPJ}`
        : `pessoa física inscrita no CPF sob o n.º ${data.clientCPF}`;
      
      addJustifiedText(`${data.clientName}, ${clientInfo}, com endereço à ${data.clientAddress}, ${data.clientCity} - ${data.clientState}, CEP: ${data.clientCEP}, doravante denominada simplesmente CLIENTE,`);

      addJustifiedText('firmam o presente CONTRATO DE FORNECIMENTO E INSTALAÇÃO DE UMA MICRO USINA FOTOVOLTAICA (doravante CONTRATO), mediante as cláusulas e condições livremente pactuadas nos termos abaixo expostos:');

      // CLÁUSULA 1
      addSection('DO OBJETO, PREÇO E CONDIÇÕES DE PAGAMENTO');
      
      addJustifiedText('1. É objeto deste contrato o fornecimento e a instalação de uma usina de microgeração fotovoltaica ("USINA"), a ser devidamente conectada à rede elétrica, cuja potência, valor e a respectiva forma e prazo de pagamento encontram-se descritos abaixo:');

      // Tabela
      const beneficiaryCount = parseInt(data.beneficiaryUnits);
      const beneficiaryText = `${data.beneficiaryUnits} (${this.numberToWords(beneficiaryCount)}) UNIDADE${beneficiaryCount > 1 ? 'S' : ''} CONSUMIDORA${beneficiaryCount > 1 ? 'S' : ''} BENEFICIÁRIA${beneficiaryCount > 1 ? 'S' : ''}`;
      
      const totalValueText = this.formatCurrency(data.totalValue) + 
        (this.formatCurrencyInWords(data.totalValue) ? ` (${this.formatCurrencyInWords(data.totalValue)})` : '');
      
      const firstPaymentText = this.formatCurrency(data.firstPaymentValue) + 
        (this.formatCurrencyInWords(data.firstPaymentValue) ? ` (${this.formatCurrencyInWords(data.firstPaymentValue)})` : '');
      
      const secondPaymentText = this.formatCurrency(data.secondPaymentValue) + 
        (this.formatCurrencyInWords(data.secondPaymentValue) ? ` (${this.formatCurrencyInWords(data.secondPaymentValue)})` : '');

      const paymentInfo1 = `1ª parcela (${data.firstPaymentPercent}%): ${firstPaymentText} pago ${this.getPaymentEventName(data.firstPaymentEvent)}`;

      const paymentInfo2 = data.firstPaymentPercent < 100 && data.secondPaymentPercent > 0
        ? `2ª parcela (${data.secondPaymentPercent}%): ${secondPaymentText} pago ${this.getPaymentEventName(data.secondPaymentEvent)}`
        : null;

      const tableData = [
        { isHeader: true, colspan: true, text: 'Potência da usina e condições de pagamento:' },
        { label: '• Potência nominal da Usina:', value: `${data.systemPower} KWp` },
        { label: '• Valor total da Usina:', value: `${totalValueText};` },
        { label: '• Forma de pagamento:', value: paymentInfo1 }
      ];

      if (paymentInfo2) {
        tableData.push({ label: '', value: paymentInfo2 });
      }

      tableData.push(
        { label: '• Vencimento das parcelas:', value: `Conforme ocorrência ${data.firstPaymentPercent >= 100 ? 'do evento mencionado acima' : 'dos eventos mencionados acima'}.` },
        { isHeader: true, colspan: true, text: 'Informações adicionais:' },
        { label: '• Nome do titular da conta de luz:', value: data.accountHolderName },
        { label: '• CPF/CNPJ:', value: data.accountHolderCPF },
        { label: '• Modalidade de compensação:', value: `${this.getCompensationModeName(data.compensationMode)} COM ${beneficiaryText}` }
      );

      addTable(tableData);

      addJustifiedText('1.1. O preço indicado acima considera que a USINA será instalada no local e sob as condições indicadas na proposta anteriormente encaminhada e aceita pelo Cliente (ANEXO I).');
      
      addJustifiedText('1.1.1. Estão incluídos no valor indicado todas as despesas de transporte, taxas e tributos incidentes.', 10);
      
      addJustifiedText('1.1.2. A realização de obras estruturais e de adequações na rede elétrica, direta ou indiretamente relacionadas a USINA, não constituem objeto deste contrato e não estão incluídas no preço.', 10);
      
      addJustifiedText('1.1.3. A EBES SOLAR poderá suspender a entrega e instalação da USINA objeto deste contrato caso se verifique, às vésperas da data acordada para a execução do contrato, que o CLIENTE encontra-se em mora com uma ou mais parcelas referentes ao pagamento acordado.', 10);

      addJustifiedText('1.2. Caso, por qualquer motivo não previsto neste instrumento, haja desistência de quaisquer das partes do negócio ora celebrado, a parte desistente deverá comunicar à outra em até 5 (cinco) dias úteis da assinatura do presente contrato e arcará com multa no valor referente a 10% (dez por cento) do valor total previsto na Cláusula 1.1.');

      addJustifiedText('1.2.1. Caso o CLIENTE deseje rescindir este contrato posteriormente ao prazo exposto no caput deste artigo (5 dias úteis da data da assinatura), deverá ele arcar com a multa anteriormente prevista, bem como ressarcir a EBES por eventuais gastos que já tenham sido realizados com o projeto -- sejam eles da ordem de recursos humanos ou materiais, desde que devidamente comprovados pela EBES.', 10);

      addJustifiedText('1.3. Esclarece-se que a USINA é composta principalmente por módulos fotovoltaicos e microinversores em quantidades e potências compatíveis com a indicada no quadro da cláusula 1., além de suportes, cabos e condutores elétricos, e elementos de fixação suficientes para a sua completa instalação e funcionamento.');

      // CLÁUSULA 2
      addSection('DO PRAZO DE ENTREGA E DA INSTALAÇÃO');
      
      addJustifiedText(`2. Fica estabelecido o prazo de ${data.installationDeadline} (${this.numberToWords(parseInt(data.installationDeadline))}) dias úteis para a instalação da USINA ora contratada, a contar da data da emissão do parecer de acesso que deverá ser protocolado o pedido em até 5 (cinco) dias úteis após a assinatura deste contrato.`);

      addJustifiedText('2.1 Não se incluem aqui os prazos requeridos pela concessionária local para a conexão da USINA à rede elétrica, realização da vistoria técnica, emissão do relatório da visita, e instalação de medidor bidirecional -- necessário para o início da operação da USINA -- sobre os quais a EBES SOLAR não tem controle e, portanto, nenhuma responsabilidade em caso de atraso em seu cumprimento.', 10);

      addJustifiedText('2.2. O CLIENTE se compromete a cumprir todas as exigências feitas pela concessionária em tempo hábil, isentando a EBES SOLAR de qualquer responsabilidade por atrasos relacionados às exigências da concessionária e/ou de terceiros (poder público e particular) relacionados ou não à USINA.');

      // CLÁUSULA 3
      addSection('DAS RESPONSABILIDADES E DIREITOS DAS PARTES');
      
      addJustifiedText('3. Constituem direitos do CLIENTE o recebimento do objeto deste CONTRATO e a sua fiscalização, nas condições avençadas, inclusive no tocante à garantia. E, da EBES SOLAR, o recebimento do preço ajustado na forma e no prazo acordados.');

      addJustifiedText('3.1. Constituem obrigações da EBES SOLAR:');
      addJustifiedText('a. Entregar e instalar a USINA de acordo com as especificações contidas na cláusula 1ª e no Anexo I;', 10);
      addJustifiedText('b. Executar a instalação da unidade geradora conforme a melhor técnica e segundo as normas aplicáveis de segurança;', 10);
      addJustifiedText('c. Atender prontamente as dúvidas do CLIENTE relativas aos materiais e serviços;', 10);
      addJustifiedText('d. Providenciar a Anotação de Responsabilidade Técnica junto ao órgão competente para fins de instalação da parte elétrica da USINA.', 10);
      addJustifiedText('e. Prover seus funcionários de equipamentos de proteção individual, necessários e suficientes à execução segura dos serviços;', 10);

      addJustifiedText('3.2. Constituem obrigações do CLIENTE:');
      addJustifiedText('a. Efetuar pontualmente o pagamento conforme pactuado neste Contrato;', 10);
      addJustifiedText('b. Receber os serviços que atendam aos requisitos deste CONTRATO e rejeitar os que estejam em desacordo com as obrigações assumidas pela EBES SOLAR, registrando por escrito o motivo de sua recusa;', 10);
      addJustifiedText('c. Disponibilizar, sem ônus para a EBES SOLAR, todas as facilidades necessárias à boa execução dos serviços;', 10);
      addJustifiedText('d. Comunicar à EBES SOLAR, por escrito, as irregularidades ou defeitos verificados na execução dos serviços;', 10);
      addJustifiedText('e. Proporcionar acesso aos funcionários da EBES SOLAR às suas dependências, quando necessário para a execução dos serviços.', 10);

      // CLÁUSULA 4
      addSection('DAS GARANTIAS');
      
      addJustifiedText(`4. A EBES SOLAR garante a USINA contra defeitos de instalação pelo prazo de ${data.warrantyPeriod} (um) ano, a contar da data da entrega da obra.`);

      addJustifiedText('4.1. A garantia compreende substituição de peças e mão de obra no reparo de defeitos devidamente constatados, pela EBES SOLAR, como sendo de instalação.', 10);

      addJustifiedText('4.2. A EBES SOLAR prestará assistência técnica e contra defeitos de fabricação, durante o prazo de garantia, sem ônus para o CLIENTE, salvo em caso de defeitos originados por força maior e/ou má utilização do equipamento.', 10);

      addJustifiedText('4.3. Expirada a garantia, a continuidade da prestação de serviços de assistência técnica e manutenção pela EBES SOLAR dependerá de nova contratação expressa entre as partes.', 10);

      addJustifiedText(`4.4. Os módulos fotovoltaicos têm garantia estendida do fabricante de ${data.moduleWarranty} (doze) anos e o(s) inversor(es) tem garantia estendida de ${data.inverterWarranty} (dez) anos, todos contra defeitos de fabricação, a contar do término da garantia exposta no artigo 9. Em sendo necessário o acesso à garantia estendida, a EBES SOLAR se compromete a prestar todo o auxílio técnico e informativo necessário para que o CLIENTE tenha o devido respaldo no contato com o fabricante, desde que o CLIENTE tenha cumprido fielmente os termos de garantia do próprio fabricante e aqueles exigidos neste contrato.`, 10);

      addJustifiedText('4.5. São hipóteses excludentes da garantia prevista neste contrato:');
      addJustifiedText('I. Mau uso ou uso da USINA de forma contrária ao preconizado no manual EBES SOLAR;', 10);
      addJustifiedText('II. A interferência direta do CLIENTE ou de terceiros não habilitados pela EBES SOLAR na USINA;', 10);
      addJustifiedText('III. A imposição de embaraços ou dificuldades para que qualquer representante da EBES SOLAR tenha acesso à USINA;', 10);
      addJustifiedText('IV. Qualquer dano na USINA originado de ocorrência na rede de transmissão da concessionária de energia elétrica, independentemente de haver ou não ressarcimento do dano pela concessionária;', 10);
      addJustifiedText('V. A ocorrência de força maior e eventos naturais, tais como, mas não exclusivamente, tempestades, rajadas de vento, furacão, ciclone, etc.', 10);

      // CLÁUSULA 5
      addSection('DAS DISPOSIÇÕES GERAIS');
      
      addJustifiedText('5. O CLIENTE declara estar ciente de que a USINA objeto deste contrato é conectada diretamente à rede da concessionária e tem como função exclusiva a geração de energia elétrica, estando seu bom ou mau desempenho diretamente relacionado à ocorrência de condições climáticas favoráveis.');

      addJustifiedText('5.1. A USINA foi dimensionada levando em consideração:');
      addJustifiedText('- a capacidade/ vontade de investimento pelo CLIENTE.', 10);
      addJustifiedText('Assim sendo, na hipótese de registro de consumo superior ao inicialmente informado, a manutenção proporcional do benefício registrado pelo CLIENTE só será possível com a readequação da potência da USINA e aquisição de novos equipamentos, que não são parte deste contrato.', 10);

      addJustifiedText('5.2. O presente contrato será considerado rescindido, sem ônus para as partes, na hipótese de ser detectada, pela EBES SOLAR, a inviabilidade técnica de instalação da USINA no imóvel do CLIENTE, ou caso a concessionária de energia vede, por qualquer motivo, a sua instalação.');

      addJustifiedText('5.3. Considerando que a EBES SOLAR não é a fabricante dos insumos da USINA, o presente contrato também poderá ser rescindido sem ônus para as partes na hipótese de alteração do cenário econômico, nacional ou internacional, que acarrete alta exagerada dos preços dos equipamentos que compõem a USINA. Nesse caso, fica a EBES SOLAR obrigada a restituir os valores eventualmente pagos pelo CLIENTE devidamente corrigidos pelo IGP-M.');

      addJustifiedText('5.4. O CLIENTE declara ter sido devidamente orientado sobre as informações contidas no "Caderno temático da ANEEL -- Micro e Minigeração Distribuída -- Sistema de Compensação de energia elétrica" disponível no site da ANEEL e no endereço eletrônico: http://bit.ly/cadernoaneel, que esclarece em detalhes o funcionamento do Sistema de Compensação de Energia Elétrica, regulamentado pela Resolução 482/2012, disponível no site da ANEEL e no endereço eletrônico http://bit.ly/resolucao482.');

      addJustifiedText('5.5. O CLIENTE desde já autoriza a EBES SOLAR a registrar e divulgar no Brasil e no exterior, gratuitamente e sem prazo definido, imagens e vídeos referentes à execução deste contrato. Autoriza, ainda, que a EBES SOLAR inclua sua logomarca em seu rol de clientes e fixe, em sua fachada, placa publicitária informando que naquele local há uma usina fotovoltaica instalada pela contratada.');

      // ==== CLÁUSULAS PERSONALIZADAS ====
      const customClauses = data.customClauses || [];
      let clauseNumber = 6;

      if (customClauses.length > 0) {
        customClauses.forEach((clause, index) => {
          addSection(clause.title);
          addJustifiedText(`${clauseNumber}. ${clause.content}`);
          clauseNumber++;
        });
      }

      // CLÁUSULA FINAL - DO FORO
      addSection('DO FORO');
      
      addJustifiedText(`${clauseNumber}. As partes elegem o foro da comarca de ${data.clientCity}, ${data.clientState}, para dirimir questões decorrentes deste Contrato, com exclusão de qualquer outro por mais privilegiado que seja.`);

      pdf.setFont('times', 'italic');
      addJustifiedText('E, por estarem assim justas e contratadas, as PARTES celebram o presente instrumento em duas vias de igual teor, na presença das duas testemunhas abaixo assinadas.');
      pdf.setFont('times', 'normal');

      // Data
      const today = new Date();
      const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
        'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
      const formattedDate = `${data.clientCity}, ${today.getDate()} de ${months[today.getMonth()].toUpperCase()} de ${today.getFullYear()}.`;
      
      yPosition += 10;
      checkSpace(30);
      pdf.setFont('times', 'italic');
      const dateWidth = pdf.getTextWidth(formattedDate);
      pdf.text(formattedDate, pageWidth - margin - dateWidth, yPosition);
      yPosition += 15;
      pdf.setFont('times', 'normal');

      // Assinaturas
      checkSpace(50);
      
      const signatureY = yPosition;
      const col1X = margin;
      const col2X = margin + (contentWidth / 2) + 10;
      const signatureWidth = 70;

      // Linha de assinatura EBES
      pdf.line(col1X, signatureY, col1X + signatureWidth, signatureY);
      pdf.setFont('times', 'bold');
      pdf.setFontSize(10);
      const ebesLines = pdf.splitTextToSize('EBES ENGENHARIA LTDA- EBES SOLAR', signatureWidth);
      let ebesY = signatureY + 5;
      ebesLines.forEach(line => {
        const lineWidth = pdf.getTextWidth(line);
        pdf.text(line, col1X + (signatureWidth - lineWidth) / 2, ebesY);
        ebesY += 4;
      });
      pdf.setFont('times', 'normal');
      pdf.setFontSize(9);
      const cnpjWidth = pdf.getTextWidth('CNPJ: 02.277.320/0002-08');
      pdf.text('CNPJ: 02.277.320/0002-08', col1X + (signatureWidth - cnpjWidth) / 2, ebesY);
      pdf.setFontSize(11);

      // Linha de assinatura CLIENTE
      pdf.line(col2X, signatureY, col2X + signatureWidth, signatureY);
      pdf.setFont('times', 'bold');
      pdf.setFontSize(10);
      const clientNameLines = pdf.splitTextToSize(data.clientName, signatureWidth);
      let clientY = signatureY + 5;
      clientNameLines.forEach(line => {
        const lineWidth = pdf.getTextWidth(line);
        pdf.text(line, col2X + (signatureWidth - lineWidth) / 2, clientY);
        clientY += 4;
      });
      pdf.setFont('times', 'normal');
      pdf.setFontSize(9);
      const clientDoc = data.clientType === 'pj' ? `CNPJ: ${data.clientCNPJ}` : `CPF: ${data.clientCPF}`;
      const docWidth = pdf.getTextWidth(clientDoc);
      pdf.text(clientDoc, col2X + (signatureWidth - docWidth) / 2, clientY);
      pdf.setFontSize(11);

      const maxY = Math.max(ebesY, clientY);
      yPosition = maxY + 15;

      // Testemunhas
      checkSpace(40);
      
      pdf.setFont('times', 'bold');
      pdf.text('TESTEMUNHAS:', margin, yPosition);
      yPosition += 10;
      pdf.setFont('times', 'normal');

      const witnessY = yPosition;

      // Testemunha 1
      pdf.line(col1X, witnessY, col1X + signatureWidth, witnessY);
      pdf.setFont('times', 'bold');
      pdf.setFontSize(10);
      const witness1Width = pdf.getTextWidth('1ª TESTEMUNHA');
      pdf.text('1ª TESTEMUNHA', col1X + (signatureWidth - witness1Width) / 2, witnessY + 5);
      pdf.setFont('times', 'normal');
      pdf.setFontSize(9);
      pdf.text('Nome: _____________________________', col1X, witnessY + 9);
      pdf.text('CPF: ______________________________', col1X, witnessY + 13);
      pdf.setFontSize(11);

      // Testemunha 2
      pdf.line(col2X, witnessY, col2X + signatureWidth, witnessY);
      pdf.setFont('times', 'bold');
      pdf.setFontSize(10);
      const witness2Width = pdf.getTextWidth('2ª TESTEMUNHA');
      pdf.text('2ª TESTEMUNHA', col2X + (signatureWidth - witness2Width) / 2, witnessY + 5);
      pdf.setFont('times', 'normal');
      pdf.setFontSize(9);
      pdf.text('Nome: _____________________________', col2X, witnessY + 9);
      pdf.text('CPF: ______________________________', col2X, witnessY + 13);
      pdf.setFontSize(11);

      // Observações
      if (data.observations) {
        yPosition = witnessY + 25;
        checkSpace(30);
        
        pdf.setFillColor(255, 243, 205);
        pdf.setDrawColor(255, 193, 7);
        const obsHeight = 20;
        pdf.rect(margin, yPosition, contentWidth, obsHeight, 'FD');
        
        pdf.setFont('times', 'bold');
        pdf.text('OBSERVAÇÕES ADICIONAIS:', margin + 3, yPosition + 5);
        pdf.setFont('times', 'normal');
        const obsLines = pdf.splitTextToSize(data.observations, contentWidth - 6);
        let obsY = yPosition + 10;
        obsLines.forEach(line => {
          pdf.text(line, margin + 3, obsY);
          obsY += 4;
        });
      }

      // Salvar PDF
      const filename = `contrato_ebes_solar_${data.clientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);

      return {
        success: true,
        fileName: filename,
        message: 'Contrato EBES Solar gerado com sucesso!'
      };

    } catch (error) {
      console.error('Erro ao gerar PDF EBES Solar:', error);
      return {
        success: false,
        error: error.message,
        message: 'Erro ao gerar o contrato EBES Solar'
      };
    }
  }
}

export default new EbesSolarPDFService();